import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useTenant } from '../lib/tenant-context';
import { tenantFetch } from '../lib/api-client';
import { 
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, BarChart, Bar, Legend 
} from 'recharts';

// Types
interface AnalyticsData {
  period: string;
  summary: {
    totalCampaigns: number;
    activeCampaigns: number;
    totalBudget: number;
    totalSpent: number;
    totalReserved: number;
    totalOffersSent: number;
    totalAccepted: number;
    totalDeclined: number;
    totalExpired: number;
    conversionRate: number;
    budgetUsedPercent: number;
  };
  creditRedemption: {
    issued: number;
    redeemed: number;
    rate: number;
  };
  dailyTrends: Array<{
    date: string;
    offersSent: number;
    accepted: number;
    declined: number;
    totalValue: number;
  }>;
  categoryBreakdown: Array<{
    category: string;
    count: number;
    accepted: number;
    totalValue: number;
    avgValue: number;
  }>;
  conditionBreakdown: Array<{
    condition: string;
    count: number;
    accepted: number;
    avgValue: number;
  }>;
  campaignPerformance: Array<{
    id: string;
    name: string;
    status: string;
    budget: number;
    spent: number;
    offersSent: number;
    accepted: number;
    creditIssued: number;
    conversionRate: string;
  }>;
}

// Icons
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M8 2v8m0 0l-3-3m3 3l3-3M3 12v1a1 1 0 001 1h8a1 1 0 001-1v-1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
    <path d="M6 2l4 4H7v4H5V6H2l4-4z" />
  </svg>
);

const ArrowDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
    <path d="M6 10l-4-4h3V2h2v4h3l-4 4z" />
  </svg>
);

// Metric Card Component
function MetricCard({ 
  label, 
  value, 
  subtext, 
  change, 
  positive,
  color = '#171717'
}: { 
  label: string; 
  value: string; 
  subtext: string;
  change?: string;
  positive?: boolean;
  color?: string;
}) {
  return (
    <div className="bg-white p-4 border" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
      <p className="text-sm mb-1" style={{ color: '#A9A9A9' }}>{label}</p>
      <p className="text-2xl font-semibold" style={{ color }}>{value}</p>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-xs" style={{ color: '#A9A9A9' }}>{subtext}</span>
        {change && (
          <span 
            className="flex items-center gap-0.5 text-xs font-medium"
            style={{ color: positive ? '#2E7D32' : '#C62828' }}
          >
            {positive ? <ArrowUpIcon /> : <ArrowDownIcon />}
            {change}
          </span>
        )}
      </div>
    </div>
  );
}

// Demo data generator
function getDemoAnalytics(tenantId: string, period: string): AnalyticsData {
  const days = period === '7d' ? 7 : period === '90d' ? 90 : 30;
  
  // Generate daily trends
  const dailyTrends = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const baseOffers = Math.floor(Math.random() * 15) + 5;
    const accepted = Math.floor(baseOffers * (0.4 + Math.random() * 0.3));
    dailyTrends.push({
      date: date.toISOString().split('T')[0],
      offersSent: baseOffers,
      accepted,
      declined: Math.floor(baseOffers * 0.2),
      totalValue: accepted * (50 + Math.random() * 100)
    });
  }

  const totalOffers = dailyTrends.reduce((sum, d) => sum + d.offersSent, 0);
  const totalAccepted = dailyTrends.reduce((sum, d) => sum + d.accepted, 0);
  const totalValue = dailyTrends.reduce((sum, d) => sum + d.totalValue, 0);

  const categories = tenantId === 'nespresso' 
    ? ['Coffee Pods', 'Accessories', 'Machines']
    : tenantId === 'lululemon'
      ? ['Leggings', 'Tops', 'Accessories', 'Outerwear']
      : ['Jackets', 'Backpacks', 'Pants', 'Accessories'];

  return {
    period,
    summary: {
      totalCampaigns: tenantId === 'nespresso' ? 1 : 3,
      activeCampaigns: tenantId === 'nespresso' ? 1 : 2,
      totalBudget: tenantId === 'nespresso' ? 5000000 : 9000000,
      totalSpent: Math.round(totalValue * 100),
      totalReserved: Math.round(totalValue * 10),
      totalOffersSent: totalOffers,
      totalAccepted,
      totalDeclined: Math.floor(totalOffers * 0.2),
      totalExpired: Math.floor(totalOffers * 0.15),
      conversionRate: Math.round((totalAccepted / totalOffers) * 100),
      budgetUsedPercent: tenantId === 'nespresso' ? 25 : 38
    },
    creditRedemption: {
      issued: Math.round(totalValue * 100),
      redeemed: Math.round(totalValue * 65),
      rate: 65
    },
    dailyTrends,
    categoryBreakdown: categories.map((cat, i) => ({
      category: cat,
      count: Math.floor(totalOffers / categories.length) + (i === 0 ? 15 : 0),
      accepted: Math.floor(totalAccepted / categories.length),
      totalValue: totalValue / categories.length,
      avgValue: 50 + i * 15
    })),
    conditionBreakdown: [
      { condition: 'Excellent', count: Math.floor(totalOffers * 0.3), accepted: Math.floor(totalAccepted * 0.35), avgValue: 85 },
      { condition: 'Good', count: Math.floor(totalOffers * 0.4), accepted: Math.floor(totalAccepted * 0.4), avgValue: 65 },
      { condition: 'Fair', count: Math.floor(totalOffers * 0.2), accepted: Math.floor(totalAccepted * 0.2), avgValue: 45 },
      { condition: 'Worn', count: Math.floor(totalOffers * 0.1), accepted: Math.floor(totalAccepted * 0.05), avgValue: 25 }
    ],
    campaignPerformance: tenantId === 'nespresso' ? [
      { id: '1', name: 'Pod Recycling Rewards', status: 'active', budget: 50000, spent: 12500, offersSent: 2500, accepted: 1875, creditIssued: 9375, conversionRate: '75.0' }
    ] : tenantId === 'lululemon' ? [
      { id: '1', name: 'Winter Trade-In', status: 'active', budget: 25000, spent: 8750, offersSent: 47, accepted: 23, creditIssued: 4025, conversionRate: '48.9' },
      { id: '2', name: 'Holiday Leggings Takeback', status: 'draft', budget: 10000, spent: 0, offersSent: 0, accepted: 0, creditIssued: 0, conversionRate: '0' }
    ] : [
      { id: '1', name: 'ReBird Trade-In', status: 'active', budget: 50000, spent: 18750, offersSent: 156, accepted: 89, creditIssued: 15575, conversionRate: '57.1' },
      { id: '2', name: 'Shell Jacket Program', status: 'active', budget: 30000, spent: 4500, offersSent: 28, accepted: 12, creditIssued: 3600, conversionRate: '42.9' },
      { id: '3', name: 'Backpack Buyback', status: 'paused', budget: 10000, spent: 2250, offersSent: 30, accepted: 18, creditIssued: 2250, conversionRate: '60.0' }
    ]
  };
}

// Format currency
function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(cents / 100);
}

// Main Component
function CampaignAnalyticsDashboard() {
  const { currentTenant } = useTenant();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    loadAnalytics();
  }, [currentTenant.id, period]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const res = await tenantFetch(`/api/v1/campaigns/analytics?period=${period}`);
      if (res.ok) {
        const result = await res.json();
        if (result.success && result.data) {
          setData(result.data);
        } else {
          setData(getDemoAnalytics(currentTenant.id, period));
        }
      } else {
        setData(getDemoAnalytics(currentTenant.id, period));
      }
    } catch {
      setData(getDemoAnalytics(currentTenant.id, period));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type: 'campaigns' | 'offers' | 'summary') => {
    try {
      const res = await tenantFetch(`/api/v1/campaigns/export?type=${type}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_export.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" 
          style={{ borderColor: currentTenant.color, borderTopColor: 'transparent' }} 
        />
      </div>
    );
  }

  const COLORS = ['#00FBA9', currentTenant.color, '#3B82F6', '#8B5CF6', '#F59E0B'];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Link to="/campaigns" className="text-sm hover:underline" style={{ color: '#A9A9A9' }}>
              Campaigns
            </Link>
            <span style={{ color: '#A9A9A9' }}>/</span>
            <h1 className="text-2xl font-semibold" style={{ color: '#171717' }}>
              ROI Analytics
            </h1>
          </div>
          <p className="text-sm" style={{ color: '#A9A9A9' }}>
            Track takeback program performance and return on investment
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Period Selector */}
          <div className="flex border overflow-hidden" style={{ borderColor: 'rgba(169,169,169,0.3)' }}>
            {(['7d', '30d', '90d'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className="px-4 py-2 text-sm font-medium transition-colors"
                style={{ 
                  backgroundColor: period === p ? '#171717' : 'transparent',
                  color: period === p ? '#fff' : '#525252'
                }}
              >
                {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
          
          {/* Export Dropdown */}
          <div className="relative group">
            <button 
              className="flex items-center gap-2 px-4 py-2 border text-sm font-medium hover:bg-gray-50 transition-colors"
              style={{ borderColor: 'rgba(169,169,169,0.3)', color: '#171717' }}
            >
              <DownloadIcon />
              Export
            </button>
            <div className="absolute right-0 top-full mt-1 bg-white border shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
              <button onClick={() => handleExport('summary')} className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-50">Summary CSV</button>
              <button onClick={() => handleExport('campaigns')} className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-50">Campaigns CSV</button>
              <button onClick={() => handleExport('offers')} className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-50">All Offers CSV</button>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total Campaigns"
          value={data.summary.activeCampaigns.toString()}
          subtext={`${data.summary.totalCampaigns} total`}
          color={currentTenant.color}
        />
        <MetricCard
          label="Budget Spent"
          value={formatCurrency(data.summary.totalSpent)}
          subtext={`${data.summary.budgetUsedPercent}% of budget`}
          change={`${data.summary.budgetUsedPercent}%`}
          positive={data.summary.budgetUsedPercent < 80}
          color="#00FBA9"
        />
        <MetricCard
          label="Offers Sent"
          value={data.summary.totalOffersSent.toLocaleString()}
          subtext={`${data.summary.totalAccepted} accepted`}
          color="#3B82F6"
        />
        <MetricCard
          label="Conversion Rate"
          value={`${data.summary.conversionRate}%`}
          subtext="Accepted / Sent"
          positive={data.summary.conversionRate > 40}
          color="#8B5CF6"
        />
      </div>

      {/* Credit Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white p-4 border" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
          <p className="text-sm mb-1" style={{ color: '#A9A9A9' }}>Credit Issued</p>
          <p className="text-xl font-semibold" style={{ color: '#171717' }}>{formatCurrency(data.creditRedemption.issued)}</p>
        </div>
        <div className="bg-white p-4 border" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
          <p className="text-sm mb-1" style={{ color: '#A9A9A9' }}>Credit Redeemed</p>
          <p className="text-xl font-semibold" style={{ color: '#00FBA9' }}>{formatCurrency(data.creditRedemption.redeemed)}</p>
        </div>
        <div className="bg-white p-4 border" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
          <p className="text-sm mb-1" style={{ color: '#A9A9A9' }}>Redemption Rate</p>
          <p className="text-xl font-semibold" style={{ color: '#171717' }}>{data.creditRedemption.rate}%</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Offers Over Time */}
        <div className="bg-white p-6 border" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
          <h2 className="text-lg font-semibold mb-4" style={{ color: '#171717' }}>Offers Over Time</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.dailyTrends}>
                <defs>
                  <linearGradient id="sentGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={currentTenant.color} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={currentTenant.color} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="acceptedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00FBA9" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#00FBA9" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#A9A9A9', fontSize: 11 }}
                  tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tick={{ fill: '#A9A9A9', fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="offersSent" name="Sent" stroke={currentTenant.color} fill="url(#sentGrad)" />
                <Area type="monotone" dataKey="accepted" name="Accepted" stroke="#00FBA9" fill="url(#acceptedGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="bg-white p-6 border" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
          <h2 className="text-lg font-semibold mb-4" style={{ color: '#171717' }}>Category Breakdown</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.categoryBreakdown}
                  dataKey="count"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percent }) => `${name || ''} ${((percent || 0) * 100).toFixed(0)}%`}
                >
                  {data.categoryBreakdown.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Condition & Credit Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Condition Breakdown */}
        <div className="bg-white p-6 border" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
          <h2 className="text-lg font-semibold mb-4" style={{ color: '#171717' }}>Items by Condition</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.conditionBreakdown}>
                <XAxis dataKey="condition" tick={{ fill: '#A9A9A9', fontSize: 11 }} />
                <YAxis tick={{ fill: '#A9A9A9', fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" name="Offers Sent" fill={currentTenant.color} />
                <Bar dataKey="accepted" name="Accepted" fill="#00FBA9" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Credit vs Redemption */}
        <div className="bg-white p-6 border" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
          <h2 className="text-lg font-semibold mb-4" style={{ color: '#171717' }}>Credit vs Redemption</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { name: 'Credit Issued', value: data.creditRedemption.issued / 100 },
                { name: 'Credit Redeemed', value: data.creditRedemption.redeemed / 100 }
              ]}>
                <XAxis dataKey="name" tick={{ fill: '#A9A9A9', fontSize: 11 }} />
                <YAxis tick={{ fill: '#A9A9A9', fontSize: 11 }} tickFormatter={(val) => `$${val.toLocaleString()}`} />
                <Tooltip formatter={(val) => `$${(val as number).toLocaleString()}`} />
                <Bar dataKey="value" fill="#00FBA9">
                  <Cell fill={currentTenant.color} />
                  <Cell fill="#00FBA9" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Campaign Performance Table */}
      <div className="bg-white border" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
        <div className="p-4 border-b" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
          <h2 className="text-lg font-semibold" style={{ color: '#171717' }}>Campaign Performance</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
                <th className="text-left p-4 text-sm font-medium" style={{ color: '#A9A9A9' }}>Campaign</th>
                <th className="text-left p-4 text-sm font-medium" style={{ color: '#A9A9A9' }}>Status</th>
                <th className="text-right p-4 text-sm font-medium" style={{ color: '#A9A9A9' }}>Budget</th>
                <th className="text-right p-4 text-sm font-medium" style={{ color: '#A9A9A9' }}>Spent</th>
                <th className="text-right p-4 text-sm font-medium" style={{ color: '#A9A9A9' }}>Offers</th>
                <th className="text-right p-4 text-sm font-medium" style={{ color: '#A9A9A9' }}>Accepted</th>
                <th className="text-right p-4 text-sm font-medium" style={{ color: '#A9A9A9' }}>Conv. Rate</th>
                <th className="text-right p-4 text-sm font-medium" style={{ color: '#A9A9A9' }}>Credit Issued</th>
              </tr>
            </thead>
            <tbody>
              {data.campaignPerformance.map((campaign) => (
                <tr key={campaign.id} className="border-b last:border-0 hover:bg-gray-50" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
                  <td className="p-4">
                    <Link 
                      to="/campaigns/$id" 
                      params={{ id: campaign.id }}
                      className="font-medium hover:underline" 
                      style={{ color: '#171717' }}
                    >
                      {campaign.name}
                    </Link>
                  </td>
                  <td className="p-4">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{ 
                        backgroundColor: campaign.status === 'active' ? '#E8F5E9' : campaign.status === 'paused' ? '#FFF3E0' : '#F5F5F5',
                        color: campaign.status === 'active' ? '#2E7D32' : campaign.status === 'paused' ? '#E65100' : '#757575'
                      }}
                    >
                      {campaign.status}
                    </span>
                  </td>
                  <td className="p-4 text-right" style={{ color: '#171717' }}>${campaign.budget.toLocaleString()}</td>
                  <td className="p-4 text-right" style={{ color: '#171717' }}>${campaign.spent.toLocaleString()}</td>
                  <td className="p-4 text-right" style={{ color: '#171717' }}>{campaign.offersSent}</td>
                  <td className="p-4 text-right" style={{ color: '#00FBA9' }}>{campaign.accepted}</td>
                  <td className="p-4 text-right" style={{ color: '#171717' }}>{campaign.conversionRate}%</td>
                  <td className="p-4 text-right font-medium" style={{ color: '#171717' }}>${campaign.creditIssued.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/campaign-analytics')({
  component: CampaignAnalyticsDashboard
});

